/**
 * Автоматическое применение миграций и система отката
 */

import { EventEmitter } from 'events';
import { 
  IMigration, 
  IMigrationManager
} from '../types/migration-interfaces';

/**
 * Конфигурация для автоматического применения миграций
 */
export interface MigrationRunnerConfig {
  // Автоматически применять миграции при старте
  autoMigrate?: boolean;
  
  // Максимальное количество попыток при ошибке
  maxRetries?: number;
  
  // Задержка между попытками (в мс)
  retryDelay?: number;
  
  // Таймаут для одной миграции (в мс)
  migrationTimeout?: number;
  
  // Создавать резервную копию перед применением
  createBackup?: boolean;
  
  // Останавливать на первой ошибке
  stopOnError?: boolean;
  
  // Режим отладки
  debug?: boolean;
  
  // Фильтр миграций для применения
  migrationFilter?: (migration: IMigration) => boolean;
}

/**
 * Информация о выполнении миграций
 */
export interface MigrationExecutionInfo {
  // Общее количество миграций
  totalMigrations: number;
  
  // Количество выполненных миграций
  completedMigrations: number;
  
  // Количество неудачных миграций
  failedMigrations: number;
  
  // Общее время выполнения
  totalExecutionTime: number;
  
  // Текущая миграция
  currentMigration?: string;
  
  // Статус выполнения
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  
  // Ошибки
  errors: Array<{
    migrationId: string;
    error: string;
    timestamp: Date;
  }>;
}

/**
 * Стратегия отката
 */
export type RollbackStrategy = 'immediate' | 'batch' | 'manual';

/**
 * Конфигурация отката
 */
export interface RollbackConfig {
  // Стратегия отката
  strategy: RollbackStrategy;
  
  // Целевая версия для отката
  targetVersion?: string;
  
  // Количество миграций для отката
  rollbackCount?: number;
  
  // Подтверждение отката
  requireConfirmation?: boolean;
  
  // Создавать резервную копию перед откатом
  createBackup?: boolean;
}

/**
 * Класс для автоматического применения миграций
 */
export class MigrationRunner extends EventEmitter {
  private migrationManager: IMigrationManager;
  private config: MigrationRunnerConfig;
  private isRunning: boolean = false;
  private currentExecution?: MigrationExecutionInfo;
  private abortController?: AbortController;

  constructor(migrationManager: IMigrationManager, config: MigrationRunnerConfig = {}) {
    super();
    this.migrationManager = migrationManager;
    this.config = {
      autoMigrate: true,
      maxRetries: 3,
      retryDelay: 1000,
      migrationTimeout: 30000,
      createBackup: true,
      stopOnError: true,
      debug: false,
      ...config
    };
  }

  /**
   * Автоматическое применение всех ожидающих миграций
   */
  async runPendingMigrations(): Promise<MigrationExecutionInfo> {
    if (this.isRunning) {
      throw new Error('Migration runner is already running');
    }

    this.isRunning = true;
    this.abortController = new AbortController();

    try {
      const status = await this.migrationManager.checkStatus();
      const pendingMigrations = status.pendingMigrations;

      this.currentExecution = {
        totalMigrations: pendingMigrations.length,
        completedMigrations: 0,
        failedMigrations: 0,
        totalExecutionTime: 0,
        status: 'running',
        errors: []
      };

      this.emit('execution:start', this.currentExecution);

      if (pendingMigrations.length === 0) {
        this.log('No pending migrations found');
        this.currentExecution.status = 'completed';
        this.emit('execution:complete', this.currentExecution);
        return this.currentExecution;
      }

      this.log(`Found ${pendingMigrations.length} pending migrations`);

      const startTime = Date.now();

      // Выполняем все ожидающие миграции через MigrationManager
      try {
        const results = await this.migrationManager.migrate();
        
        // Обновляем статистику
        this.currentExecution.completedMigrations = results.filter(r => r.success).length;
        this.currentExecution.failedMigrations = results.filter(r => !r.success).length;
        
        // Добавляем ошибки
        for (const result of results) {
          if (!result.success) {
            this.currentExecution.errors.push({
              migrationId: 'unknown',
              error: result.error || 'Unknown error',
              timestamp: new Date()
            });
          }
        }
        
        if (this.abortController.signal.aborted) {
          this.currentExecution.status = 'cancelled';
        }
      } catch (error) {
        this.currentExecution.failedMigrations = this.currentExecution.totalMigrations;
        this.currentExecution.errors.push({
          migrationId: 'unknown',
          error: error instanceof Error ? error.message : 'Unknown error',
          timestamp: new Date()
        });
        this.currentExecution.status = 'failed';
      }

      this.currentExecution.totalExecutionTime = Date.now() - startTime;

      if (this.currentExecution.status === 'running') {
        this.currentExecution.status = this.currentExecution.failedMigrations > 0 ? 'failed' : 'completed';
      }

      this.emit('execution:complete', this.currentExecution);
      this.log(`Migration execution completed. Status: ${this.currentExecution.status}`);

      return this.currentExecution;

    } finally {
      this.isRunning = false;
      this.abortController = undefined;
    }
  }





  /**
   * Откат миграций
   */
  async rollback(config: RollbackConfig): Promise<MigrationExecutionInfo> {
    if (this.isRunning) {
      throw new Error('Migration runner is already running');
    }

    this.isRunning = true;
    this.abortController = new AbortController();

    try {
      this.log(`Starting rollback with strategy: ${config.strategy}`);

      let targetVersion = config.targetVersion;
      
      if (!targetVersion) {
        // Определяем целевую версию автоматически
        const history = await this.migrationManager.getHistory();
        const completedMigrations = history
          .filter(r => r.status === 'completed')
          .sort((a, b) => b.completedAt!.getTime() - a.completedAt!.getTime());

        if (completedMigrations.length === 0) {
          throw new Error('No migrations to rollback');
        }

        const rollbackCount = config.rollbackCount || 1;
        const targetRecord = completedMigrations[rollbackCount - 1];
        
        if (!targetRecord) {
          throw new Error(`Cannot rollback ${rollbackCount} migrations`);
        }

        targetVersion = targetRecord.version;
      }

      this.log(`Rolling back to version: ${targetVersion}`);

      // Выполняем откат
      const results = await this.migrationManager.rollback(targetVersion);

      // Создаем информацию о выполнении
      const executionInfo: MigrationExecutionInfo = {
        totalMigrations: results.length,
        completedMigrations: results.filter(r => r.success).length,
        failedMigrations: results.filter(r => !r.success).length,
        totalExecutionTime: results.reduce((sum, r) => sum + (r.executionTime || 0), 0),
        status: results.every(r => r.success) ? 'completed' : 'failed',
        errors: results
          .filter(r => !r.success)
          .map(r => ({
            migrationId: 'unknown',
            error: r.error || 'Unknown error',
            timestamp: new Date()
          }))
      };

      this.emit('rollback:complete', executionInfo);
      this.log(`Rollback completed. Status: ${executionInfo.status}`);

      return executionInfo;

    } finally {
      this.isRunning = false;
      this.abortController = undefined;
    }
  }

  /**
   * Отмена выполнения миграций
   */
  async cancel(): Promise<void> {
    if (!this.isRunning) {
      return;
    }

    this.log('Cancelling migration execution');
    this.abortController?.abort();
    
    // Ждем завершения
    while (this.isRunning) {
      await this.sleep(100);
    }

    this.emit('execution:cancelled');
  }

  /**
   * Проверка возможности отката
   */
  async canRollback(targetVersion?: string): Promise<boolean> {
    try {
      const history = await this.migrationManager.getHistory();
      const completedMigrations = history.filter(r => r.status === 'completed');

      if (completedMigrations.length === 0) {
        return false;
      }

      if (targetVersion) {
        // Проверяем, что целевая версия существует в истории
        return completedMigrations.some(r => r.version === targetVersion);
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Получение текущего статуса выполнения
   */
  getCurrentExecution(): MigrationExecutionInfo | undefined {
    return this.currentExecution;
  }

  /**
   * Проверка, выполняется ли миграция
   */
  isExecuting(): boolean {
    return this.isRunning;
  }

  /**
   * Логирование
   */
  private log(message: string): void {
    if (this.config.debug) {
      console.log(`[MigrationRunner] ${message}`);
    }
    this.emit('log', { message, timestamp: new Date() });
  }

  /**
   * Задержка
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
} 